import React, { useEffect, useState } from 'react';
import { t } from '../i18n';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { listenSites, listenPlayerLocations, listenLocationHistory } from '../firebase';

// Fix leaflet default icon path when using CRA
try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
  });
} catch (e) {
  // ignore
}

const ICON_BASE = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/';
const AVAILABLE_COLORS = ['blue','green','red','yellow','orange','violet','grey','black'];

const ICON_CACHE = {};
function iconForColor(color) {
  if (!color) color = 'blue';
  if (ICON_CACHE[color]) return ICON_CACHE[color];
  const icon = L.icon({ iconUrl: ICON_BASE + `marker-icon-2x-${color}.png`, shadowUrl: require('leaflet/dist/images/marker-shadow.png'), iconSize: [25,41], iconAnchor: [12,41] });
  ICON_CACHE[color] = icon;
  return icon;
}

export default function AdminMap() {
  const [sites, setSites] = useState([]);
  const [playerLocations, setPlayerLocations] = useState([]);
  const [histories, setHistories] = useState({});
  const unsubsRef = React.useRef({});

  useEffect(() => {
    const unsub = listenSites(setSites);
    return () => unsub();
  }, []);

  useEffect(() => {
    // Only listen to player locations, not admin
    const unsub = listenPlayerLocations(locations => {
      // Filter out admin user if present
      const adminUid = typeof window !== 'undefined' ? localStorage.getItem('adminUid') : null;
      const filtered = locations.filter(p => p.uid !== adminUid && p.id !== adminUid);
      setPlayerLocations(filtered);
    });
    return () => unsub();
  }, []);

  // Maintain per-player history listeners. When playerLocations changes, attach listeners for new
  // players and remove listeners for players that disappeared.
  useEffect(() => {
    const uids = new Set(playerLocations.map(p => p.uid || p.id));
    playerLocations.forEach(p => {
      const uid = p.uid || p.id;
      if (!uid) return;
      if (!unsubsRef.current[uid]) {
        const unsub = listenLocationHistory(uid, (items) => {
          setHistories(prev => ({ ...prev, [uid]: items }));
        }, 1000);
        unsubsRef.current[uid] = unsub;
      }
    });
    Object.keys(unsubsRef.current).forEach(uid => {
      if (!uids.has(uid)) {
        try { unsubsRef.current[uid](); } catch (e) {}
        delete unsubsRef.current[uid];
        setHistories(prev => { const copy = { ...prev }; delete copy[uid]; return copy; });
      }
    });
    return () => {
      Object.values(unsubsRef.current).forEach(u => { try { u(); } catch (e) {} });
      unsubsRef.current = {};
    };
  }, [playerLocations]);

  const center = sites.length ? [sites[0].location.lat, sites[0].location.lng] : [59.437, 24.7536];
  function hashString(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = ((h << 5) - h) + str.charCodeAt(i);
    return Math.abs(h);
  }
  function getColorForTeam(teamId) {
    if (!teamId) return 'blue';
    const idx = hashString(teamId) % AVAILABLE_COLORS.length;
    return AVAILABLE_COLORS[idx];
  }

  // Map layout fills available space below navbar, no title/header
  return (
    <div className="admin-map-container" style={{ width: '100%', height: 'calc(100vh - 56px)', marginTop: 0 }}>
      <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
        {sites.map(s => (
          s.location && s.location.lat && s.location.lng ? (
            <Marker key={`site-${s.id}`} position={[s.location.lat, s.location.lng]} icon={iconForColor('blue')}>
              <Popup>
                <div>
                  <strong>{s.title}</strong>
                  <div className="small text-muted">{s.description}</div>
                </div>
              </Popup>
            </Marker>
          ) : null
        ))}
        {Object.entries(histories).map(([uid, pts]) => {
          if (!pts || pts.length < 2) return null;
          const player = playerLocations.find(pl => (pl.uid || pl.id) === uid) || {};
          const color = getColorForTeam(player.teamId || (pts[0] && pts[0].teamId) || '');
          const latlngs = pts.map(p => [p.lat, p.lng]);
          return <Polyline key={`poly-${uid}`} positions={latlngs} pathOptions={{ color }} />;
        })}
        {playerLocations.map(p => (
          p.lat && p.lng ? (
            <Marker key={p.uid || p.id} position={[p.lat, p.lng]} icon={iconForColor(getColorForTeam(p.teamId || ''))}>
              <Popup>
                <div>
                  <div><strong>{p.displayName || p.uid}</strong></div>
                  <div className="small text-muted">{t('team')}: {p.teamId || '—'}</div>
                  <div className="small text-muted">{t('updated')}: {p.updatedAt ? new Date(p.updatedAt.seconds * 1000).toLocaleString() : '—'}</div>
                </div>
              </Popup>
            </Marker>
          ) : null
        ))}
      </MapContainer>
    </div>
  );
}
